/**
 * Delegation Monitor Page (Sprint 250 H5, ISS-514).
 *
 * Lists delegation tasks for a conversation (status filter), shows their
 * lifecycle and allows cancelling in-flight background delegations.
 * Uses the tenant-scoped delegation endpoints (fixed hook routes).
 */

import { useState } from 'react';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  SearchInput,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
} from '@/components/ui';
import {
  useDelegationTasks,
  useCancelDelegationTask,
} from '@/features/agents/hives/hooks/useDelegationTasks';
import type { DelegationTaskStatus } from '@/features/agents/hives/types';

const STATUS_OPTIONS: Array<DelegationTaskStatus | ''> = [
  '',
  'pending',
  'dispatched',
  'running',
  'completed',
  'failed',
  'timeout',
];

const STATUS_STYLES: Record<string, string> = {
  completed: 'text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-300',
  failed: 'text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300',
  timeout: 'text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300',
  running: 'text-blue-700 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-300',
  dispatched: 'text-blue-700 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-300',
  pending: 'text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
};

function formatDuration(ms?: number): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export default function DelegationMonitorPage() {
  const { t } = useI18n();
  const [conversationInput, setConversationInput] = useState('');
  const [appliedConversationId, setAppliedConversationId] = useState('');
  const [status, setStatus] = useState<DelegationTaskStatus | ''>('');

  const { data, isLoading, isError } = useDelegationTasks(
    appliedConversationId || undefined,
    { status: status || undefined, limit: 50 }
  );
  const cancelMutation = useCancelDelegationTask();

  const tasks = data?.data ?? [];

  const handleCancel = (taskId: string) => {
    if (!window.confirm(t('admin.delegations.cancelConfirm'))) return;
    cancelMutation.mutate(taskId);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          {t('admin.delegations.title')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('admin.delegations.subtitle')}
        </p>
      </div>

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex-1">
            <SearchInput
              value={conversationInput}
              onChange={(event) => setConversationInput(event.target.value)}
              placeholder={t('admin.delegations.conversationId')}
            />
          </div>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as DelegationTaskStatus | '')}
            aria-label={t('admin.delegations.status')}
            className="h-10 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 text-sm"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option || 'all'} value={option}>
                {option || t('admin.delegations.all')}
              </option>
            ))}
          </select>
          <Button
            onClick={() => setAppliedConversationId(conversationInput.trim())}
            data-testid="delegation-search-button"
          >
            {t('admin.delegations.search')}
          </Button>
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.delegations.status')}</TableHead>
              <TableHead>{t('admin.delegations.mode')}</TableHead>
              <TableHead>{t('admin.delegations.task')}</TableHead>
              <TableHead>{t('admin.delegations.depth')}</TableHead>
              <TableHead>{t('admin.delegations.duration')}</TableHead>
              <TableHead>{t('admin.delegations.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!appliedConversationId ? (
              <TableEmpty colSpan={6} message={t('admin.delegations.enterConversation')} />
            ) : isLoading ? (
              <TableEmpty colSpan={6} message={t('common.loading')} />
            ) : isError ? (
              <TableEmpty colSpan={6} message={t('admin.delegations.error')} />
            ) : tasks.length === 0 ? (
              <TableEmpty colSpan={6} message={t('admin.delegations.empty')} />
            ) : (
              tasks.map((task) => (
                <TableRow key={task.id} data-testid={`delegation-row-${task.id}`}>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        STATUS_STYLES[task.status] ?? STATUS_STYLES.pending
                      }`}
                    >
                      {task.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-gray-600 dark:text-gray-400">
                    {task.handoff_type} / {task.mode}
                    {task.fanout_group_id ? ` (${t('admin.delegations.fanout')})` : ''}
                  </TableCell>
                  <TableCell className="text-gray-600 dark:text-gray-400">
                    <span className="line-clamp-2 max-w-md">
                      {task.task_description || '—'}
                    </span>
                  </TableCell>
                  <TableCell className="text-gray-600 dark:text-gray-400 tabular-nums">
                    {task.depth}
                  </TableCell>
                  <TableCell className="text-gray-600 dark:text-gray-400 tabular-nums">
                    {formatDuration(task.duration_ms)}
                  </TableCell>
                  <TableCell>
                    {['pending', 'dispatched', 'running'].includes(task.status) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancel(task.id)}
                        disabled={cancelMutation.isPending}
                        data-testid={`delegation-cancel-${task.id}`}
                      >
                        {t('admin.delegations.cancel')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
